import {MAX_MAILBOX_LENGTH, matchesMailboxGrammar} from "../core/validation/mailbox";
import type {FeedbackMessages} from "../i18n/en/feedback";

/**
 * Pure Support/Feedback draft validation (ui#326, epic #218 "logic outside React"): the client-side
 * mirror of every rejection the feedback RPC can raise, so the sender is stopped AT THE FIELD they can
 * fix instead of by one opaque "couldn't be sent" toast on which a retry can never succeed.
 *
 * The verdict carries error KEYS, never translated copy — {@link feedbackFieldErrorText} resolves them
 * against a catalog the caller supplies, so this module holds no i18n concern and no React import.
 */

/**
 * A key into the `feedback.fieldErrors` catalog namespace. Derived from the English catalog so that
 * renaming or dropping a message is a compile error here rather than a silent runtime `undefined`.
 */
export type FeedbackFieldErrorKey = keyof FeedbackMessages["feedback"]["fieldErrors"];

/**
 * Longest note the api will accept, mirroring `FeedbackRequest.message` `@Size(max = 512)`
 * (kitecorp/transferify-api-service, `features/feedback/FeedbackRequest.java`).
 *
 * It is a deliberate product limit on a support note, no longer a transport one: since
 * api-service#217 the message is rendered into the MIME body in-repo rather than riding in Mailjet's
 * templated-variable header, which is what originally forced the bound. If the api moves it, this
 * must move with it — `feedbackValidation.test.ts` pins the number as a literal on this side so the
 * two cannot drift silently.
 *
 * Counted in UTF-16 code units, exactly as Java's `String.length()` counts them, so the two agree on
 * a Romanian diacritic (1 unit each side) and on an emoji (2 units each side).
 */
export const MAX_FEEDBACK_MESSAGE_LENGTH = 512;

/**
 * Longest reply-to address the api will accept: the shared {@link MAX_MAILBOX_LENGTH} (the SMTP
 * total-length limit `Mailboxes.MAXIMUM_LENGTH`). Aliased under the reply-to name the feedback catalog
 * and this module's test already speak, so the number has a single source (ui#362).
 */
export const MAX_REPLY_TO_LENGTH = MAX_MAILBOX_LENGTH;

/** The draft fields the rules read, in the shape `FeedbackApi.send` posts them. */
export interface FeedbackDraft {
	message: string;
	replyTo: string;
}

/**
 * The verdict, per FIELD rather than as a single first-failure: a sender whose note is too long AND
 * whose address is malformed must be able to fix both in one pass instead of discovering the second
 * problem only after resubmitting.
 */
export interface FeedbackFieldErrors {
	message: FeedbackFieldErrorKey | null;
	replyTo: FeedbackFieldErrorKey | null;
}

/** A draft with nothing wrong with it — also the reset value for a freshly opened dialog. */
export const NO_FEEDBACK_FIELD_ERRORS: FeedbackFieldErrors = {message: null, replyTo: null};

/**
 * The address as it should be POSTed. Trimming is load-bearing rather than cosmetic: the api accepts
 * an EMPTY `replyTo` as "no reply address given" but rejects a whitespace-only one (`@Pattern` matches
 * "" and not " "), so an untrimmed blank field would be a guaranteed 400 on the anonymous messages the
 * public contact form exists to accept.
 */
export function normalizeReplyTo(value: string): string {
	return value.trim();
}

/**
 * Validates a feedback draft against every rejection the api can raise.
 *
 * `maxMessageLength` exists because the posted body is not always the typed one: choosing a topic
 * prepends an inbox tag (`core/support/feedbackTopic.ts`), and the api's `@Size` measures what it
 * RECEIVES. The caller passes the budget that is actually left, so the sender is stopped at the
 * length they really have instead of meeting a 400 no retry could clear (ui#326's whole point).
 * Defaulted, so every existing caller and its tests keep the plain 512-character rule.
 *
 * @example
 * validateFeedbackDraft({message: "hi", replyTo: "user@localhost"});
 * // => {message: null, replyTo: "replyToInvalid"}
 */
export function validateFeedbackDraft(
	draft: FeedbackDraft,
	maxMessageLength: number = MAX_FEEDBACK_MESSAGE_LENGTH,
): FeedbackFieldErrors {
	return {
		message: messageErrorOf(draft.message, maxMessageLength),
		replyTo: replyToErrorOf(draft.replyTo),
	};
}

/**
 * Whether the note has text: what arms the dialog's Send button.
 *
 * Complete is not VALID. A complete draft can still be refused by {@link validateFeedbackDraft} (a note
 * over the length budget, a malformed reply-to), and that refusal belongs at the field, where it can
 * name the fix, rather than in a button that stays off without a reason.
 *
 * It also says nothing about a send in flight (kitecorp/transferify-ui#3079). The view DISABLES the
 * button for an incomplete draft, and a disabled button drops the keyboard focus it holds out of the
 * dialog, so the in-flight state is reported separately and the button stays focusable through it.
 */
export function isFeedbackDraftComplete(draft: Pick<FeedbackDraft, "message">): boolean {
	return !isBlank(draft.message);
}

/** Whether a verdict blocks the send. */
export function hasFeedbackFieldError(errors: FeedbackFieldErrors): boolean {
	return errors.message !== null || errors.replyTo !== null;
}

/**
 * Resolves one field-error key against the catalog's `feedback.fieldErrors` namespace.
 *
 * A function rather than a bare `fieldErrors[key]` index because the namespace mixes plain strings
 * with `(max) => string` builders: indexing it generically types as `string | ((max) => string)` and,
 * through an untyped toast/text slot, renders the BUILDER instead of the message. Resolving here also
 * keeps the api's bounds out of the copy, so the number can only ever come from the constants above.
 */
export function feedbackFieldErrorText(
	fieldErrors: FeedbackMessages["feedback"]["fieldErrors"],
	key: FeedbackFieldErrorKey,
	maxMessageLength: number = MAX_FEEDBACK_MESSAGE_LENGTH,
): string {
	switch (key) {
		case "emptyMessage":
			return fieldErrors.emptyMessage;
		case "messageTooLong":
			// The budget the sender actually has, which a chosen topic shrinks — quoting the raw api bound
			// here would name a limit the field itself refuses to reach.
			return fieldErrors.messageTooLong(maxMessageLength);
		case "replyToInvalid":
			return fieldErrors.replyToInvalid;
		case "replyToTooLong":
			return fieldErrors.replyToTooLong(MAX_REPLY_TO_LENGTH);
	}
}

/** `@NotBlank` then `@Size(max = 512)`, in the api's own order. */
function messageErrorOf(message: string, maxMessageLength: number): FeedbackFieldErrorKey | null {
	if (isBlank(message)) {
		return "emptyMessage";
	}
	// The RAW length, because `@Size` measures the raw string the request carries, not a trimmed one.
	if (message.length > maxMessageLength) {
		return "messageTooLong";
	}
	return null;
}

/**
 * The address is OPTIONAL — absent is a valid answer on a public contact form. A present one is held
 * to the full grammar, with length reported separately so an address that is merely too long is not
 * described to the sender as malformed.
 */
function replyToErrorOf(replyTo: string): FeedbackFieldErrorKey | null {
	const address = normalizeReplyTo(replyTo);
	if (address.length === 0) {
		return null;
	}
	if (address.length > MAX_REPLY_TO_LENGTH) {
		return "replyToTooLong";
	}
	return matchesMailboxGrammar(address) ? null : "replyToInvalid";
}

/** The api's `@NotBlank`: nothing but whitespace counts as empty. */
function isBlank(value: string): boolean {
	return value.trim().length === 0;
}
